import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { Loader2 } from "lucide-react";

export default function StepEditDialog({ goalId, step, onClose, onDone }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [name, setName] = useState("");
  const [estMin, setEstMin] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (step) {
      setName(step.name || "");
      setEstMin(step.est_minutes != null ? String(step.est_minutes) : "");
      setDue(step.due?.slice(0, 10) || "");
    }
  }, [step]);

  if (!step) return null;

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.patch(
        `/career/goals/${goalId}/steps/${step.step_id}`,
        {
          name: name.trim(),
          est_minutes: estMin ? parseInt(estMin, 10) : null,
          due: due || null,
        }
      );
      toast.success(t("step.saved"));
      onDone?.(r.data);
      onClose?.();
    } catch { toast.error(t("step.save_fail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!step} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="step-edit-dialog"
        className="max-w-md bg-stone-950 border-stone-800">
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {t("step.edit.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <div>
            <label className="label-cap block mb-1">{t("step.name_ph")}</label>
            <input
              autoFocus
              data-testid="step-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("step.name_ph")}
              className="script-input w-full"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="label-cap block mb-1">{t("step.time_min")}</label>
              <input
                data-testid="step-edit-min"
                type="number" min="0" step="5"
                value={estMin}
                onChange={(e) => setEstMin(e.target.value)}
                className="script-input w-full"
              />
            </div>
            <div className="flex-1">
              <label className="label-cap block mb-1">{t("step.due")}</label>
              <input
                data-testid="step-edit-due"
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                className="script-input w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 flex-wrap">
          <button onClick={onClose} className="ghost-btn">{t("action.cancel")}</button>
          <button
            onClick={submit}
            data-testid="step-edit-save"
            disabled={saving || !name.trim()}
            className="pill-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {t("action.save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
